<script setup>
import KeywordPositionCell from "@frontJs/components/tables/cells/projectManagement/seo/KeywordPositionCell.vue";
import TrackingPositionActionsCell from "@frontJs/components/tables/cells/projectManagement/seo/TrackingPositionActionsCell.vue";
import { ArrowPathIcon } from "@heroicons/vue/24/solid";
import ActionButton from "@js/components/ActionButton.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CheckCircleIconCell from "@js/components/tables/cells/CheckCircleIconCell.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatDate } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h, ref } from "vue";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const loadingForce = ref(false);

const { tenant } = defineProps({
  tenant: {
    type: Object,
    required: true,
  },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  data,
  count,
  pagination,
  pageCount,
  sorting,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "keyword",
      header: () => t("all.label.Keyword"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.keyword,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "google_website",
      header: () => t("all.label.googleWebsite"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.google_website,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "website_variant",
      header: () => t("all.label.websiteVariant"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.website_variant,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "position",
      header: () => t("all.label.Position"),
      cell: (props) =>
        h(KeywordPositionCell, {
          keyword: props.row.original,
          tenant: tenant.id,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "search_volume",
      header: () => t("all.label.Search volume"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.search_volume,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "url",
      header: () => t("all.label.Url"),
      cell: (props) =>
        (props.row.original.url &&
          h(LinkCellV2, {
            text: props.row.original.url,
            href: props.row.original.url,
          })) ||
        h(TextCellV2, {
          text: "-",
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "important_to_follow",
      header: () => t("label.importantToFollow"),
      cell: (props) =>
        props.row.original.important_to_follow ? h(CheckCircleIconCell) : null,
    }),
    columnHelper.accessor(() => null, {
      id: "date",
      header: () => t("all.label.Updated at"),
      cell: (props) =>
        h(TextCellV2, {
          text: formatDate(props.row.original.date),
        }),
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      headerClasses: "justify-center",
      cell: (props) =>
        h(TrackingPositionActionsCell, {
          row: props.row,
        }),
    }),
  ],
  columnPinning: {
    right: ["actions"],
  },
  updateData,
});

function updateData() {
  loading.value = true;

  let url = route("front.seo.tracking_position.keywords.get_tracked");

  let postData = {
    tenant_id: tenant.id,
    pageIndex: pagination.value.pageIndex,
    pageSize: pagination.value.pageSize,
    sorting: sorting.value,
    globalFilter: globalFilter.value,
    filters: filters.value,
  };

  let config = {};

  axios
    .post(url, postData, config)
    .then((response) => {
      data.value = response.data.data;
      count.value = response.data.count;
      pagination.value.pageIndex = response.data.pageIndex;
      pagination.value.pageSize = response.data.pageSize;
      pageCount.value = response.data.pageCount;
      availableFilters.value = response.data.availableFilters;

      if (Array.isArray(availableFilters.value)) {
        table
          .getColumn("google_website")
          .toggleVisibility(
            availableFilters.value.some(({ id }) => id === "google_website"),
          );

        table
          .getColumn("website_variant")
          .toggleVisibility(
            availableFilters.value.some(({ id }) => id === "website_variant"),
          );
      }
    })
    .catch((error) => {
      console.log(error);
    })
    .finally(() => {
      loading.value = false;
    });
}

sorting.value = [
  {
    id: "position",
    desc: false,
  },
];

function loadTrackedKeywords() {
  const url = route("front.seo.tracking_position.keywords.force_load");
  loadingForce.value = true;
  axios
    .post(url, { tenant_id: tenant.id })
    .then(() => {
      toast.success(t("all.text.Launch of update"));
    })
    .catch((error) => {
      console.log(error);
      toast.error(t("all.text.An error has occurred"));
    })
    .finally(() => {
      loading.value = false;
      loadingForce.value = false;
    });
}

const loadPositionHistory = () => {};
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <div v-else>
    <DataTableLayout
      :table="table"
      :count="count"
      :global-filter="globalFilter"
      :set-global-filter="setGlobalFilter"
      :available-filters="availableFilters"
      :filters="filters"
      :set-filters="setFilters"
    >
      <template #actions>
        <ActionButton
          :left-icon="ArrowPathIcon"
          size="small"
          :loading="loadingForce"
          :disabled="loadingForce"
          :callback="() => loadTrackedKeywords()"
        >
          {{ t("all.label.update") }}
        </ActionButton>
      </template>
    </DataTableLayout>
  </div>
</template>
